import { useState, useEffect, useRef, type ChangeEvent, type DragEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Tabs from '../components/tabs/Tabs';
import Spinner from '../components/Spinner/spinner';
import Image from '../components/image/Image';
import Icon from '../components/icon/Icon';
import ReactModal from 'react-modal';
import {
  getApprovedLines,
  getOngoingMonetization,
  getSyndicatedLines,
  annulMonetization,
  executeGuaranteesImport,
  processEpymeImport,
  processEpymePromissoryNotesImport,
  type ExecuteImportResponse,
  type EpymeImportResponse,
} from '../services/GuaranteesService';
import { useConfig } from '../context/ConfigContext';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { mapSyndicatedLines, SYNDICATED_MONETIZATION_TOAST_ID, type SyndicatedLine } from '../utils/syndicatedLines';
import { getSgrData } from '../services/SgrService';
import { routes } from '../routes/routePaths';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import Span from '../components/texts/span/Span';
import Tooltip from '../components/tooltip/Tooltip';
import { getMonetizationInstrumentLabel, type MonetizationInstrumentType } from '../utils/monetizationInstrument';
import { NewRequestWizard } from '../organisms/MonetizationRequest';
import AnnulMonetizationDialog from '../components/dialogs/AnnulMonetizationDialog';
import GuaranteeAnnulActions from '../components/guarantees/GuaranteeAnnulActions';
import { Toast, useToast } from '../components/toast/toast';
import { resolveAnnulInstrumentType } from '../utils/guaranteeAnnulUtils';
import { currencyDisplayLabel, resolveCurrencyCode } from '../utils/currencyLabel';

const SGR_CUIT_UNAVAILABLE_MESSAGE =
  'No se pudo obtener el identificador fiscal de la SGR. Configurá Mis datos de empresa para este tenant o contactá soporte.';

const EPYME_MOTIVO_LABELS: Record<string, string> = {
  NO_EXISTE_LINEA_PARA_CUIT: 'No existe línea para el identificador fiscal',
  SALDO_INSUFICIENTE: 'Saldo insuficiente',
  SGR_NO_COINCIDE: 'El identificador fiscal de la SGR no coincide',
  ERROR_TECNICO: 'Error técnico',
  CUIT_INVALIDO: 'Identificador fiscal inválido',
  IMPORTE_INVALIDO: 'Importe inválido',
  FECHA_INVALIDA: 'Fecha inválida',
  MONEDA_INVALIDA: 'Moneda inválida',
  CARACTER_INVALIDO: 'Carácter inválido',
  CHEQUE_DUPLICADO: 'Cheque duplicado',
  DUPLICADO: 'Duplicado',
  SIN_FONDO_INTEGRADO: 'Sin fondo integrado disponible',
  FONDO_INTEGRADO_INSUFICIENTE: 'Fondo integrado insuficiente',
};

const formatEpymeMotivo = (motivo: string): string =>
  EPYME_MOTIVO_LABELS[motivo] ?? motivo.replace(/_/g, ' ');

interface TableRow {
  application_number: number;
  application_product_line_id: number;
  product_line_code: string;
  cuit: string;
  product_line_label: string;
  id_currency: number;
  currency_label: string;
  approved_amount: number;
  monetization_amount: number;
  available_amount: number;
  company_name: string;
}

interface MonetizationRow {
  id: number;
  row_key: string;
  type: MonetizationInstrumentType;
  id_global: string;
  cuit: string;
  company_name: string;
  product_line_label: string;
  application_product_line_id?: number;
  creditor: string;
  issue_date: string;
  due_date: string;
  currency_label: string;
  monetization_amount: number;
  monetization_status_type_id?: number;
}

const Monetization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const fiscalId = useFiscalIdConfig();
  const showSyndicatedTab = config.isSyndicatedPublicOperationsEnabled === true;
  const syndicatedCreatedIdGlobal = (location.state as { syndicatedCreatedIdGlobal?: string } | null)
    ?.syndicatedCreatedIdGlobal;
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [itemsPerPage] = useState<number>(10);
  const [tablePaginationData, setTablePaginationData] = useState<any>(null);
  
  // Check URL params for tab selection
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState<number>(tabParam ? parseInt(tabParam) : 0);

  // Estados para el tab "En gestión"
  const [ongoingSearchText, setOngoingSearchText] = useState<string>('');
  const [isOngoingSearching, setIsOngoingSearching] = useState<boolean>(false);
  const [hasOngoingSearched, setHasOngoingSearched] = useState<boolean>(false);
  const [ongoingTableData, setOngoingTableData] = useState<MonetizationRow[]>([]);
  const [ongoingCurrentPage, setOngoingCurrentPage] = useState<number>(1);
  const [ongoingPaginationData, setOngoingPaginationData] = useState<any>(null);
  const [annulDialogOpen, setAnnulDialogOpen] = useState(false);
  const [selectedOngoingRow, setSelectedOngoingRow] = useState<MonetizationRow | null>(null);
  const [annulling, setAnnulling] = useState(false);

  const [syndicatedSearchText, setSyndicatedSearchText] = useState('');
  const [isSyndicatedSearching, setIsSyndicatedSearching] = useState(false);
  const [hasSyndicatedSearched, setHasSyndicatedSearched] = useState(false);
  const [syndicatedLines, setSyndicatedLines] = useState<SyndicatedLine[]>([]);

  const mavFileInputRef = useRef<HTMLInputElement>(null);
  const [mavFile, setMavFile] = useState<File | null>(null);
  const [mavDragging, setMavDragging] = useState(false);
  const [mavProcessing, setMavProcessing] = useState(false);
  const [mavError, setMavError] = useState('');
  const [mavResult, setMavResult] = useState<ExecuteImportResponse | null>(null);

  // E-Pyme states
  const epymeFileInputRef = useRef<HTMLInputElement>(null);
  const [epymeFile, setEpymeFile] = useState<File | null>(null);
  const [epymeDragging, setEpymeDragging] = useState(false);
  const [epymeProcessing, setEpymeProcessing] = useState(false);
  const [epymeError, setEpymeError] = useState('');
  const [epymeResult, setEpymeResult] = useState<EpymeImportResponse | null>(null);

  const promissoryNotesFileInputRef = useRef<HTMLInputElement>(null);
  const [promissoryNotesFile, setPromissoryNotesFile] = useState<File | null>(null);
  const [promissoryNotesDragging, setPromissoryNotesDragging] = useState(false);
  const [promissoryNotesProcessing, setPromissoryNotesProcessing] = useState(false);
  const [promissoryNotesError, setPromissoryNotesError] = useState('');
  const [promissoryNotesResult, setPromissoryNotesResult] = useState<EpymeImportResponse | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultModalType, setResultModalType] = useState<'cheques' | 'pagares' | 'mav' | null>(null);
  const [sgrCuit, setSgrCuit] = useState<string>('');

  const pickMavFile = (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx')) {
      setMavError('Solo se admiten archivos .xlsx');
      return;
    }
    setMavError('');
    setMavFile(file);
    setMavResult(null);
  };

  const handleMavFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      pickMavFile(file);
    }
  };

  const handleMavDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMavDragging(true);
  };

  const handleMavDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMavDragging(false);
  };

  const handleMavDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMavDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      pickMavFile(file);
    }
  };

  const handleMavProcess = async () => {
    if (!mavFile) {
      return;
    }

    let activeSgrCuit = sgrCuit;
    if (!activeSgrCuit) {
      try {
        const sgrResponse = await getSgrData();
        if (sgrResponse.success && sgrResponse.data?.cuit) {
          activeSgrCuit = sgrResponse.data.cuit;
          setSgrCuit(activeSgrCuit);
        }
      } catch (_) { /* ignored, handled below */ }
    }
    if (!activeSgrCuit) {
      setMavError(SGR_CUIT_UNAVAILABLE_MESSAGE);
      return;
    }

    setMavProcessing(true);
    setMavError('');
    setMavResult(null);
    try {
      const formData = new FormData();
      formData.append('file', mavFile);
      formData.append('sgrCuit', activeSgrCuit);
      const response = await executeGuaranteesImport(formData);
      if (response.success && response.data) {
        setMavResult(response.data as ExecuteImportResponse);
        setResultModalType('mav');
        setResultModalOpen(true);
      } else {
        setMavError(
          typeof response.message === 'string' && response.message
            ? response.message
            : 'No se pudo procesar el archivo. Intentá nuevamente.'
        );
      }
    } catch (err) {
      console.error('MAV import error:', err);
      setMavError('Error de red al procesar el archivo.');
    } finally {
      setMavProcessing(false);
    }
  };

  const clearMavSelection = () => {
    setMavFile(null);
    setMavResult(null);
    setMavError('');
  };

  // E-Pyme functions
  const pickEpymeFile = (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx')) {
      setEpymeError('Solo se admiten archivos .xlsx');
      return;
    }
    setEpymeError('');
    setEpymeFile(file);
    setEpymeResult(null);
  };

  const handleEpymeFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      pickEpymeFile(file);
    }
  };

  const handleEpymeDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setEpymeDragging(true);
  };

  const handleEpymeDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setEpymeDragging(false);
  };

  const handleEpymeDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setEpymeDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      pickEpymeFile(file);
    }
  };

  const handleEpymeProcess = async () => {
    if (!epymeFile) return;

    let activeSgrCuit = sgrCuit;
    if (!activeSgrCuit) {
      try {
        const sgrResponse = await getSgrData();
        if (sgrResponse.success && sgrResponse.data?.cuit) {
          activeSgrCuit = sgrResponse.data.cuit;
          setSgrCuit(activeSgrCuit);
        }
      } catch (_) { /* ignored, handled below */ }
    }
    if (!activeSgrCuit) {
      setEpymeError(SGR_CUIT_UNAVAILABLE_MESSAGE);
      return;
    }

    setEpymeProcessing(true);
    setEpymeError('');
    setEpymeResult(null);
    try {
      const response = await processEpymeImport(epymeFile, activeSgrCuit);
      if (response.success && response.data) {
        setEpymeResult(response.data as EpymeImportResponse);
        setResultModalType('cheques');
        setResultModalOpen(true);
      } else {
        setEpymeError(
          typeof response.message === 'string' && response.message
            ? response.message
            : 'No se pudo procesar el archivo. Intentá nuevamente.'
        );
      }
    } catch (err) {
      console.error('E-Pyme import error:', err);
      setEpymeError('Error de red al procesar el archivo.');
    } finally {
      setEpymeProcessing(false);
    }
  };

  const clearEpymeSelection = () => {
    setEpymeFile(null);
    setEpymeResult(null);
    setEpymeError('');
  };

  // Promissory notes handlers
  const pickPromissoryNotesFile = (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx')) {
      setPromissoryNotesError('Solo se admiten archivos .xlsx');
      return;
    }
    setPromissoryNotesError('');
    setPromissoryNotesFile(file);
    setPromissoryNotesResult(null);
  };

  const handlePromissoryNotesFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      pickPromissoryNotesFile(file);
    }
  };

  const handlePromissoryNotesDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPromissoryNotesDragging(true);
  };

  const handlePromissoryNotesDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPromissoryNotesDragging(false);
  };

  const handlePromissoryNotesDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPromissoryNotesDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      pickPromissoryNotesFile(file);
    }
  };

  const handlePromissoryNotesProcess = async () => {
    if (!promissoryNotesFile) return;

    let activeSgrCuit = sgrCuit;
    if (!activeSgrCuit) {
      try {
        const sgrResponse = await getSgrData();
        if (sgrResponse.success && sgrResponse.data?.cuit) {
          activeSgrCuit = sgrResponse.data.cuit;
          setSgrCuit(activeSgrCuit);
        }
      } catch (_) { /* ignored, handled below */ }
    }
    if (!activeSgrCuit) {
      setPromissoryNotesError(SGR_CUIT_UNAVAILABLE_MESSAGE);
      return;
    }

    setPromissoryNotesProcessing(true);
    setPromissoryNotesError('');
    setPromissoryNotesResult(null);
    try {
      const response = await processEpymePromissoryNotesImport(promissoryNotesFile, activeSgrCuit);
      if (response.success && response.data) {
        setPromissoryNotesResult(response.data as EpymeImportResponse);
        setResultModalType('pagares');
        setResultModalOpen(true);
      } else {
        setPromissoryNotesError(
          typeof response.message === 'string' && response.message
            ? response.message
            : 'No se pudo procesar el archivo. Intentá nuevamente.'
        );
      }
    } catch (err) {
      console.error('E-Pyme promissory notes import error:', err);
      setPromissoryNotesError('Error de red al procesar el archivo.');
    } finally {
      setPromissoryNotesProcessing(false);
    }
  };

  const clearPromissoryNotesSelection = () => {
    setPromissoryNotesFile(null);
    setPromissoryNotesResult(null);
    setPromissoryNotesError('');
  };

  // Load SGR CUIT on mount
  useEffect(() => {
    const loadSgrCuit = async () => {
      try {
        const response = await getSgrData();
        if (response.success && response.data?.cuit) {
          setSgrCuit(response.data.cuit);
        }
      } catch (err) {
        console.error('Error loading SGR CUIT:', err);
      }
    };
    loadSgrCuit();
  }, []);

  // Cargar datos automáticamente cuando se cambia al tab "En gestión"
  useEffect(() => {
    if (activeTab === 1 && !hasOngoingSearched) {
      loadOngoingData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!syndicatedCreatedIdGlobal) return;

    useToast.success({
      id: SYNDICATED_MONETIZATION_TOAST_ID,
      title: 'Aval sindicado creado',
      description: `N° ${syndicatedCreatedIdGlobal}`,
    });

    setActiveTab(3);
    setSyndicatedSearchText('');
    setSyndicatedLines([]);
    setHasSyndicatedSearched(false);

    navigate(`${routes.monetization}?tab=3`, { replace: true, state: {} });
  }, [syndicatedCreatedIdGlobal, navigate]);

  const loadOngoingData = async () => {
    setIsOngoingSearching(true);
    setHasOngoingSearched(true);

    try {
      const response = await getOngoingMonetization('', ongoingCurrentPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        setOngoingTableData(data.content || []);
        setOngoingPaginationData({
          size: data.size,
          page: ongoingCurrentPage - 1, // Convert to 0-based for Table component
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
      } else {
        setOngoingTableData([]);
        setOngoingPaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching ongoing monetization:', error);
      setOngoingTableData([]);
      setOngoingPaginationData(null);
    } finally {
      setIsOngoingSearching(false);
    }
  };

  const formatNumber = (amount: number) => {
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCurrencyName = (currencyLabel: string) => {
    const mapped = currencyDisplayLabel(currencyLabel);
    if (mapped) return mapped;
    return currencyLabel;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleSearch = async (keyCode?: number) => {
    // Only search when Enter key is pressed or button is clicked
    if (keyCode && keyCode !== 13) {
      return;
    }

    if (!searchText.trim()) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      // Remove hyphens from CUIT if present
      let processedSearchText = searchText.trim();
      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }

      const response = await getApprovedLines(processedSearchText, currentPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        
        // Use the product_lines array directly from the new response
        setTableData(data.product_lines || []);
        setTablePaginationData({
          size: data.size,
          page: data.page,
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
        setCurrentPage(data.page || 0);
      } else {
        setTableData([]);
        setTablePaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching approved lines:', error);
      setTableData([]);
      setTablePaginationData(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlusClick = (row: TableRow) => {
    navigate(`${routes.newMonetization}?product_line_id=${row.application_product_line_id}&from=monetization`);
  };

  const handleRowClicked = (row: TableRow) => {
    navigate(`${routes.newMonetization}?product_line_id=${row.application_product_line_id}&from=monetization`);
  };

  // Función para buscar en "En gestión"
  const handleOngoingSearch = async (keyCode?: number) => {
    // Only search when Enter key is pressed or button is clicked
    if (keyCode && keyCode !== 13) {
      return;
    }

    if (!ongoingSearchText.trim()) {
      // Si no hay texto, cargar todos los datos
      await loadOngoingData();
      return;
    }

    setIsOngoingSearching(true);
    setHasOngoingSearched(true);
    const searchPage = 1;
    setOngoingCurrentPage(searchPage);

    try {
      const response = await getOngoingMonetization(ongoingSearchText, searchPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        setOngoingTableData(data.content || []);
        setOngoingPaginationData({
          size: data.size,
          page: searchPage - 1, // Convert to 0-based for Table component
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
      } else {
        setOngoingTableData([]);
        setOngoingPaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching ongoing monetization:', error);
      setOngoingTableData([]);
      setOngoingPaginationData(null);
    } finally {
      setIsOngoingSearching(false);
    }
  };

  const handleOngoingChangePage = (newPage: number) => {
    // newPage comes from Table component (0-based), convert to 1-based for our logic
    const actualPage = newPage + 1;
    if (ongoingPaginationData && actualPage >= 1 && actualPage <= ongoingPaginationData.totalPages) {
      setOngoingCurrentPage(actualPage);
      // Re-fetch data with new page
      setIsOngoingSearching(true);
      const searchText = ongoingSearchText.trim();
      getOngoingMonetization(searchText, actualPage, itemsPerPage)
        .then((response) => {
          if (response.success && 'data' in response) {
            const data = response.data;
            setOngoingTableData(data.content || []);
            setOngoingPaginationData({
              size: data.size,
              page: newPage, // newPage is already 0-based from Table component
              totalElements: data.total_elements,
              totalPages: data.total_pages,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching ongoing monetization:', error);
        })
        .finally(() => {
          setIsOngoingSearching(false);
        });
    }
  };

  const handleOngoingRowClick = (row: MonetizationRow) => {
    const operationTypeByInstrument: Record<MonetizationRow['type'], string> = {
      loan: 'préstamo',
      check: 'cheque',
      promissory_note: 'pagaré',
      commercial_endorsement: 'commercial-endorsements',
    };

    const operationType = operationTypeByInstrument[row.type];
    
    // Navegar a la vista de edición con los parámetros necesarios
    navigate(`/monetizacion/edit/${row.id_global}?operation_type=${operationType}`);
  };

  const handleOngoingAnnulClick = (row: MonetizationRow) => {
    setSelectedOngoingRow(row);
    setAnnulDialogOpen(true);
  };

  const reloadOngoingData = async () => {
    if (!ongoingSearchText.trim()) {
      await loadOngoingData();
      return;
    }

    setIsOngoingSearching(true);
    try {
      const response = await getOngoingMonetization(ongoingSearchText, ongoingCurrentPage, itemsPerPage);
      if (response.success && 'data' in response) {
        const data = response.data;
        setOngoingTableData(data.content || []);
        setOngoingPaginationData({
          size: data.size,
          page: ongoingCurrentPage - 1,
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
      }
    } catch (error) {
      console.error('Error refreshing ongoing monetization:', error);
    } finally {
      setIsOngoingSearching(false);
    }
  };

  const handleOngoingAnnulConfirm = async () => {
    if (!selectedOngoingRow) return;

    setAnnulling(true);
    try {
      const type = resolveAnnulInstrumentType(selectedOngoingRow.type);
      const response = await annulMonetization(type, selectedOngoingRow.id);
      if (response.success) {
        setAnnulDialogOpen(false);
        setSelectedOngoingRow(null);
        useToast.success({
          title: 'Aval anulado correctamente',
          description: 'El monto fue liberado y ya está disponible nuevamente.',
          id: 'annul-aval-success',
          closeButton: true,
          autoClose: 5000,
          toastId: 'annul-aval-success',
        });
        await reloadOngoingData();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'No se pudo anular el aval';
      useToast.error({
        title: 'Error al anular el aval',
        description: message,
        id: 'annul-aval-error',
        closeButton: true,
        autoClose: 7000,
        toastId: 'annul-aval-error',
      });
    } finally {
      setAnnulling(false);
    }
  };

  // Columnas para la tabla de "En gestión"
  const ongoingColumns = [
    {
      name: 'Nº Aval',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.id_global}
          </P>
        </Card>
      ),
      width: '7%',
    },
    {
      name: fiscalId.label,
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {fiscalId.format(row.cuit)}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Razón social',
      selector: (row: MonetizationRow) => (
        <Card>
          <Tooltip position='top' msg={row.company_name}>
            <P type='3' variant='neutral' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
              {row.company_name}
            </P>
          </Tooltip>
        </Card>
      ),
      width: '15%',
    },
    {
      name: 'Instrumento',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {getMonetizationInstrumentLabel(row.type)}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Acreedor',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.creditor}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'F. emisión',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.issue_date)}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: 'F. vencimiento',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.due_date)}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Divisa',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.currency_label}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Bruto',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.monetization_amount != null ? `$ ${formatNumber(row.monetization_amount)}` : '-'}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: '',
      selector: (row: MonetizationRow) => row.id,
      cell: (row: MonetizationRow) => (
        <Card display='flex' gap='8px' alignItems='center' justifyContent='center'>
          <Icon
            name='arrow_right_outline'
            variant='info'
            onClick={() => handleOngoingRowClick(row)}
          />
          <GuaranteeAnnulActions
            monetizationStatusTypeId={row.monetization_status_type_id}
            onAnnulClick={() => handleOngoingAnnulClick(row)}
          />
        </Card>
      ),
      width: '8%',
      minWidth: '80px',
      center: true,
      ignoreRowClick: true,
    },
  ];

  const columns = [
    {
      name: fiscalId.label,
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {fiscalId.format(row.cuit)}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: 'Razón social',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.company_name}
          </P>
        </Card>
      ),
      width: '16%',
    },
    {
      name: 'Línea',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.application_product_line_id}
          </P>
        </Card>
      ),
      width: '7%',
    },
    {
      name: 'Instrumento',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.product_line_label}
          </P>
        </Card>
      ),
      width: '13%',
    },
    {
      name: 'Divisa',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {getCurrencyName(row.currency_label)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Total',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.approved_amount)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Utilizado',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.monetization_amount)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Disponible',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.available_amount)}
          </P>
        </Card>
      ),
      width: '13%',
    },
    {
      cell: (row: TableRow) => {
        const isDisabled = Number(row.available_amount) < 0.01;
        return (
          <Icon 
            name='plus_icon' 
            variant={isDisabled ? 'neutral' : 'info'}
            modifier={isDisabled ? 'weak' : undefined}
            onClick={isDisabled ? undefined : () => handlePlusClick(row)}
          />
        );
      },
      button: true,
      width: '5%',
      minWidth: '80px',
      center: true,
      ignoreRowClick: true,
    },
  ];

  // Pagination - now handled by server
  const handleChangePage = (newPage: number) => {
    if (tablePaginationData && newPage >= 0 && newPage < tablePaginationData.totalPages) {
      setCurrentPage(newPage);
      // Re-fetch data with new page
      setIsSearching(true);
      let processedSearchText = searchText.trim();
      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }
      
      getApprovedLines(processedSearchText, newPage, itemsPerPage)
        .then((response) => {
          if (response.success && 'data' in response) {
            const data = response.data;
            setTableData(data.product_lines || []);
            setTablePaginationData({
              size: data.size,
              page: data.page,
              totalElements: data.total_elements,
              totalPages: data.total_pages,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching approved lines:', error);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  };

  const handleSyndicatedSearch = () => {
    const query = syndicatedSearchText.trim();
    if (!query) return;
    setIsSyndicatedSearching(true);
    setHasSyndicatedSearched(true);
    getSyndicatedLines(query)
      .then((response: any) => {
        const payload = response?.success ? response.data : response;
        setSyndicatedLines(mapSyndicatedLines(payload));
      })
      .catch(() => setSyndicatedLines([]))
      .finally(() => setIsSyndicatedSearching(false));
  };

  const syndicatedColumns = [
    { name: 'Producto', selector: (row: SyndicatedLine) => row.productLabel },
    { name: 'N° de línea', selector: (row: SyndicatedLine) => row.applicationId },
    { name: 'Divisa', selector: (row: SyndicatedLine) => row.currencyCode },
    { name: 'Monto', selector: (row: SyndicatedLine) => row.lineAmount },
    { name: fiscalId.label, selector: (row: SyndicatedLine) => fiscalId.format(row.cuit) },
    { name: 'Razón social', selector: (row: SyndicatedLine) => row.companyName },
  ];

  const syndicatedTab = {
    label: 'Sindicados',
    content: (
      <>
        <Card width='400px' display='flex' gap='16px' margin={{ bottom: '3' }}>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder={fiscalId.companySearchPlaceholder}
            value={syndicatedSearchText}
            onChange={(e) => setSyndicatedSearchText(e.target.value)}
            onKeyup={(e) => {
              if (e.keyCode === 13) handleSyndicatedSearch();
            }}
          />
          <Button variant='tertiary' size='sm' onClick={handleSyndicatedSearch}>
            {isSyndicatedSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
          </Button>
        </Card>
        {isSyndicatedSearching ? (
          <Card margin={{ top: '4' }}>
            <TableSkeleton rows={8} columns={syndicatedColumns.length} />
          </Card>
        ) : hasSyndicatedSearched && syndicatedLines.length > 0 ? (
          <Card margin={{ top: '2' }}>
            <H type='3' weight='bold'>Líneas habilitadas</H>
            <Table
              columns={syndicatedColumns}
              data={syndicatedLines}
              keyField='applicationId'
              tableData={{
                page: 1,
                size: syndicatedLines.length,
                totalElements: syndicatedLines.length,
                totalPages: 1,
              }}
              disablePagination
              onRowClicked={(row: SyndicatedLine) =>
                navigate(
                  `${routes.newSyndicatedMonetization}?application_id=${row.applicationId}`,
                  { state: { line: row } }
                )
              }
              variant='normal'
            />
          </Card>
        ) : hasSyndicatedSearched ? (
          <Card display='grid' justifyContent='center' margin={{ top: '11', bottom: '11' }} width='53vw'>
            <P variant='primary' align='center' withMargin={false} weight='bold'>
              No encontramos líneas sindicadas pendientes.
            </P>
          </Card>
        ) : null}
      </>
    ),
  };

  const tabs = [
    {
      label: 'Nuevo aval',
      content: <NewRequestWizard />,
    },
    {
      label: 'En gestión',
      content: (
        <>
          {/* Search Section */}
          <Card width='400px' display='flex' gap='16px' margin={{ bottom: '3' }}>
            <Input
              type='text'
              size='md'
              icons={{ iconLeft: 'search_outline' }}
              placeholder='Buscar cliente'
              value={ongoingSearchText}
              onChange={(e) => setOngoingSearchText(e.target.value)}
              onKeyup={(e) => handleOngoingSearch(e.keyCode)}
            />
            <Button variant='tertiary' size='sm' onClick={() => handleOngoingSearch(13)}>
              {isOngoingSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
            </Button>
          </Card>

          {/* Table Section */}
          {isOngoingSearching ? (
            <Card margin={{ top: '4' }}>
              <TableSkeleton rows={10} columns={ongoingColumns.length} />
            </Card>
          ) : hasOngoingSearched && ongoingTableData.length > 0 ? (
            <Card margin={{ top: '2' }} justifyContent='center' alignItems='center'>
              <Table
                columns={ongoingColumns}
                data={ongoingTableData}
                keyField='row_key'
                tableData={ongoingPaginationData}
                handleChangePage={(page: number) => handleOngoingChangePage(page)}
                onRowClicked={handleOngoingRowClick}
                variant='normal'
              />
            </Card>
          ) : hasOngoingSearched && ongoingTableData.length === 0 ? (
            <Card
              display='grid'
              justifyContent='center'
              margin={{ top: '11', bottom: '11' }}
              width='53vw'
            >
              <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
                <Image name='folder_empty' />
              </Card>
              <P variant='primary' align='center' withMargin={false} weight='bold'>
                No encontramos resultados.
              </P>
              <P variant='primary' align='center'>
                Podés hacer una nueva búsqueda.
              </P>
            </Card>
          ) : null}
        </>
      ),
    },
    {
      label: 'Procesos masivos de cheques y pagarés',
      content: (
        <>
          <Card display='flex' justifyContent='flex-end' margin={{ bottom: '4' }}>
            <Button
              variant='secondary'
              size='md'
              icon='clock_outline'
              iconAlign='left'
              onClick={() => navigate(routes.processingHistory)}
              style={{ borderRadius: '8px', gap: '8px', padding: '8px 20px' }}
            >
              Historial de procesos
            </Button>
          </Card>

          {/* Frame E-Pyme — cheques + pagarés side by side */}
          <H type='3' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 24 }}>
            Procesar E-Pyme
          </H>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {/* — Cheques sub-block — */}
            <div>
            <Card display='flex' flexDirection='column' gap='12px'>
              <P type='2' weight='bold' variant='neutral' withMargin={false} modifier='strong'>
                Cheques
              </P>
              <input
                ref={epymeFileInputRef}
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                style={{ display: 'none' }}
                onChange={handleEpymeFileChange}
              />
              <div
                role='presentation'
                onClick={() => { if (!epymeFile) epymeFileInputRef.current?.click(); }}
                onDragOver={handleEpymeDragOver}
                onDragLeave={handleEpymeDragLeave}
                onDrop={handleEpymeDrop}
                style={{
                  border: `2px dashed ${epymeDragging ? '#03BAA1' : '#DFE6EB'}`,
                  borderRadius: 12,
                  padding: '32px 16px',
                  textAlign: 'center',
                  cursor: epymeFile ? 'default' : 'pointer',
                  background: epymeDragging ? 'rgba(3, 186, 161, 0.04)' : '#FAFBFC',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {!epymeFile && (
                  <>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#E6F5F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <Icon name='upload_cloud_outline' variant='primary' size='md' />
                    </div>
                    <P type='3' variant='neutral' withMargin={false} modifier='weak'>
                      <span style={{ color: 'var(--color-primary, #03BAA1)', fontWeight: 600, cursor: 'pointer' }}>Hacé click para cargar</span> o arrastrá y soltá el Cheques E-Pyme
                    </P>
                    <P type='4' variant='neutral' withMargin={false} modifier='weak'>
                      Formato .xlsx
                    </P>
                  </>
                )}
                {epymeFile && (
                  <>
                    <Card display='flex' alignItems='center' gap='8px'>
                      <Icon name='document_outline' variant='neutral' size='md' />
                      <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                        {epymeFile.name}
                      </P>
                    </Card>
                    <Card display='flex' gap='12px' alignItems='center' style={{ marginTop: 8 }}>
                    <Button
                      variant='primary'
                      size='sm'
                      disabled={epymeProcessing}
                      onClick={(e) => { e.stopPropagation(); void handleEpymeProcess(); }}
                      style={{ borderRadius: '8px', padding: '8px 20px', gap: '8px' }}
                    >
                      <Icon name='arrow_up_outline' variant='neutral' modifier='inverted' width={18} height={18} />
                      {epymeProcessing ? 'Procesando...' : 'Procesar Cheques'}
                    </Button>
                      <Button variant='secondary' size='sm' icon='delete_outline' iconAlign='center' disabled={epymeProcessing} onClick={(e) => { e.stopPropagation(); clearEpymeSelection(); }} style={{ borderRadius: '8px', padding: '10px', width: '40px', height: '40px', boxShadow: 'inset 0 0 0 1px #DFE6EB' }} />
                    </Card>
                  </>
                )}
                {epymeProcessing && <Spinner size='sm' variant='primary' />}
              </div>

              {epymeError ? (
                <Span variant='danger' type='2'>{epymeError}</Span>
              ) : null}
            </Card>

            </div>

            {/* — Pagarés sub-block — */}
            <div>
            <Card display='flex' flexDirection='column' gap='12px'>
              <P type='2' weight='bold' variant='neutral' withMargin={false} modifier='strong'>
                Pagarés
              </P>
              <input
                ref={promissoryNotesFileInputRef}
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                style={{ display: 'none' }}
                onChange={handlePromissoryNotesFileChange}
              />
              <div
                role='presentation'
                onClick={() => { if (!promissoryNotesFile) promissoryNotesFileInputRef.current?.click(); }}
                onDragOver={handlePromissoryNotesDragOver}
                onDragLeave={handlePromissoryNotesDragLeave}
                onDrop={handlePromissoryNotesDrop}
                style={{
                  border: `2px dashed ${promissoryNotesDragging ? '#03BAA1' : '#DFE6EB'}`,
                  borderRadius: 12,
                  padding: '32px 16px',
                  textAlign: 'center',
                  cursor: promissoryNotesFile ? 'default' : 'pointer',
                  background: promissoryNotesDragging ? 'rgba(3, 186, 161, 0.04)' : '#FAFBFC',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {!promissoryNotesFile && (
                  <>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#E6F5F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <Icon name='upload_cloud_outline' variant='primary' size='md' />
                    </div>
                    <P type='3' variant='neutral' withMargin={false} modifier='weak'>
                      <span style={{ color: 'var(--color-primary, #03BAA1)', fontWeight: 600, cursor: 'pointer' }}>Hacé click para cargar</span> o arrastrá y soltá el Pagarés E-Pyme
                    </P>
                    <P type='4' variant='neutral' withMargin={false} modifier='weak'>
                      Formato .xlsx
                    </P>
                  </>
                )}
                {promissoryNotesFile && (
                  <>
                    <Card display='flex' alignItems='center' gap='8px'>
                      <Icon name='document_outline' variant='neutral' size='md' />
                      <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                        {promissoryNotesFile.name}
                      </P>
                    </Card>
                    <Card display='flex' gap='12px' alignItems='center' style={{ marginTop: 8 }}>
                    <Button
                      variant='primary'
                      size='sm'
                      disabled={promissoryNotesProcessing}
                      onClick={(e) => { e.stopPropagation(); void handlePromissoryNotesProcess(); }}
                      style={{ borderRadius: '8px', padding: '8px 20px', gap: '8px' }}
                    >
                      <Icon name='arrow_up_outline' variant='neutral' modifier='inverted' width={18} height={18} />
                      {promissoryNotesProcessing ? 'Procesando...' : 'Procesar Pagarés'}
                    </Button>
                      <Button variant='secondary' size='sm' icon='delete_outline' iconAlign='center' disabled={promissoryNotesProcessing} onClick={(e) => { e.stopPropagation(); clearPromissoryNotesSelection(); }} style={{ borderRadius: '8px', padding: '10px', width: '40px', height: '40px', boxShadow: 'inset 0 0 0 1px #DFE6EB' }} />
                    </Card>
                  </>
                )}
                {promissoryNotesProcessing && <Spinner size='sm' variant='primary' />}
              </div>

              {promissoryNotesError ? (
                <Span variant='danger' type='2'>{promissoryNotesError}</Span>
              ) : null}
            </Card>

            </div>
          </div>

          {/* Frame MAV */}
          <H type='3' weight='bold' modifier='strong' withMargin={false} style={{ marginTop: 32, marginBottom: 24 }}>
            Procesar MAV
          </H>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <Card display='flex' flexDirection='column' gap='12px'>
            <input
              ref={mavFileInputRef}
              type='file'
              accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              style={{ display: 'none' }}
              onChange={handleMavFileChange}
            />
            <div
              role='presentation'
              onClick={() => { if (!mavFile) mavFileInputRef.current?.click(); }}
              onDragOver={handleMavDragOver}
              onDragLeave={handleMavDragLeave}
              onDrop={handleMavDrop}
              style={{
                border: `2px dashed ${mavDragging ? '#03BAA1' : '#DFE6EB'}`,
                borderRadius: 12,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: mavFile ? 'default' : 'pointer',
                background: mavDragging ? 'rgba(3, 186, 161, 0.04)' : '#FAFBFC',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {!mavFile && (
                <>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#E6F5F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <Icon name='upload_cloud_outline' variant='primary' size='md' />
                  </div>
                  <P type='3' variant='neutral' withMargin={false} modifier='weak'>
                    <span style={{ color: 'var(--color-primary, #03BAA1)', fontWeight: 600, cursor: 'pointer' }}>Hacé click para cargar</span> o arrastrá y soltá el archivo MAV
                  </P>
                  <P type='4' variant='neutral' withMargin={false} modifier='weak'>
                    Formato .xlsx
                  </P>
                </>
              )}
              {mavFile && (
                <>
                  <Card display='flex' alignItems='center' gap='8px'>
                    <Icon name='document_outline' variant='neutral' size='md' />
                    <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                      {mavFile.name}
                    </P>
                  </Card>
                  <Card display='flex' gap='12px' alignItems='center' style={{ marginTop: 8 }}>
                <Button
                  variant='primary'
                  size='sm'
                  disabled={mavProcessing}
                  onClick={(e) => { e.stopPropagation(); void handleMavProcess(); }}
                  style={{ borderRadius: '8px', padding: '8px 20px', gap: '8px' }}
                >
                  <Icon name='arrow_up_outline' variant='neutral' modifier='inverted' width={18} height={18} />
                  {mavProcessing ? 'Procesando...' : 'Procesar MAV'}
                </Button>
                    <Button variant='secondary' size='sm' icon='delete_outline' iconAlign='center' disabled={mavProcessing} onClick={(e) => { e.stopPropagation(); clearMavSelection(); }} style={{ borderRadius: '8px', padding: '10px', width: '40px', height: '40px', boxShadow: 'inset 0 0 0 1px #DFE6EB' }} />
                  </Card>
                </>
              )}
              {mavProcessing && <Spinner size='sm' variant='primary' />}
              </div>
          </Card>
          <div />
          </div>

          {mavError ? (
            <Card margin={{ bottom: '3' }}>
              <Span variant='danger' type='2'>
                {mavError}
              </Span>
            </Card>
          ) : null}

        </>
      ),
    },
    ...(showSyndicatedTab ? [syndicatedTab] : []),
  ];

  return (
    <>
      <div style={{ width: '85vw' }}><PageBreadcrumb items={[{ label: 'Inicio', to: routes.dashboard }, { label: 'Monetización' }]} /></div>
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id={SYNDICATED_MONETIZATION_TOAST_ID} />
      <Card display='flex' alignItems='center' margin={{ bottom: '4' }}>
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Monetización
        </H>
      </Card>

      <Tabs 
        tabs={tabs} 
        defaultActiveTab={0}
        selectedTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Processing Result Modal */}
      {resultModalOpen && (() => {
        const isEpyme = resultModalType === 'cheques' || resultModalType === 'pagares';
        const epymeData = resultModalType === 'cheques' ? epymeResult : resultModalType === 'pagares' ? promissoryNotesResult : null;
        const modalTitle = resultModalType === 'cheques'
          ? 'Resultado del procesamiento - Cheques E-Pyme'
          : resultModalType === 'pagares'
            ? 'Resultado del procesamiento - Pagarés E-Pyme'
            : 'Resultado del procesamiento - MAV';

        return (
          <ReactModal
            isOpen={resultModalOpen}
            onRequestClose={() => setResultModalOpen(false)}
            style={{
              overlay: { background: 'rgba(0,0,0,0.65)', zIndex: 10 },
              content: {
                alignSelf: 'center',
                borderRadius: '16px',
                width: '900px',
                height: 'auto',
                maxHeight: '85vh',
                margin: '0 auto',
                padding: '0',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            shouldFocusAfterRender={false}
            ariaHideApp={false}
          >
            <div style={{ padding: '28px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <P type='2' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                {modalTitle}
              </P>
              <Icon name='cancel_outline' cursor='pointer' onClick={() => setResultModalOpen(false)} variant='neutral' />
            </div>

            <div style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'hidden', flex: 1 }}>
              {isEpyme && epymeData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ backgroundColor: '#F5F7FA', borderRadius: '12px', padding: '20px 24px' }}>
                    <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 16 }}>
                      Resumen de registros procesados
                    </P>
                    <div style={{ display: 'flex', gap: '40px' }}>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{epymeData.total_rows}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Total</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{epymeData.loaded_count}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Ingresados</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{epymeData.rejected_count}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>No ingresados</P>
                      </div>
                    </div>
                  </div>
                  {Object.keys(epymeData.rejections_by_reason || {}).length > 0 && (
                    <div style={{ backgroundColor: '#F5F7FA', borderRadius: '12px', padding: '20px 24px' }}>
                      <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 16 }}>
                        Desglose por motivo
                      </P>
                      <div style={{ display: 'flex', gap: '40px' }}>
                        {Object.entries(epymeData.rejections_by_reason).map(([motivo, count]) => (
                          <div key={motivo}>
                            <H type='1' weight='bold' modifier='strong' withMargin={false}>{count}</H>
                            <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>{formatEpymeMotivo(motivo)}</P>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!isEpyme && mavResult && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ backgroundColor: '#F5F7FA', borderRadius: '12px', padding: '20px 24px' }}>
                    <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 16 }}>
                      Resumen de registros procesados
                    </P>
                    <div style={{ display: 'flex', gap: '40px' }}>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.total_rows}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Total</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.checks_updated + mavResult.promissory_notes_updated}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Actualizados</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.not_found}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>No encontrados</P>
                      </div>
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#F5F7FA', borderRadius: '12px', padding: '20px 24px' }}>
                    <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 16 }}>
                      Desglose
                    </P>
                    <div style={{ display: 'flex', gap: '40px' }}>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.checks_updated}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Cheques</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.promissory_notes_updated}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Pagarés</P>
                      </div>
                      <div>
                        <H type='1' weight='bold' modifier='strong' withMargin={false}>{mavResult.error_count}</H>
                        <P type='3' variant='neutral' weight='bold' modifier='strong' withMargin={false}>Con error</P>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error detail scrollable area */}
              {(() => {
                const errors = isEpyme && epymeData
                  ? epymeData.errors
                  : (!isEpyme && mavResult ? mavResult.errors : []);
                if (!errors || errors.length === 0) return null;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0 }}>
                    <P type='3' weight='bold' variant='neutral' modifier='strong' withMargin={false}>
                      Detalle de errores
                    </P>
                    <div style={{
                      border: '1px solid #DFE6EB',
                      borderRadius: '8px',
                      padding: '16px',
                      overflowY: 'auto',
                      maxHeight: '300px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}>
                      {isEpyme && epymeData
                        ? epymeData.errors.map((row, idx) => (
                            <P key={`${row.echeck_number}-${idx}`} type='3' variant='neutral' withMargin={false}>
                              Fila {row.row_number} - {row.echeck_number}: {row.motivo}. Monto: {row.monto?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}, {row.detalle}
                            </P>
                          ))
                        : mavResult?.errors.map((row, idx) => (
                            <P key={`${row.instrument_code}-${idx}`} type='3' variant='neutral' withMargin={false}>
                              {row.instrument_code} [{row.operation}] — {row.reason}{row.detail ? `: ${row.detail}` : ''}
                            </P>
                          ))
                      }
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={{ padding: '20px 32px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='secondary' onClick={() => setResultModalOpen(false)} style={{ borderRadius: '8px', padding: '10px 28px' }}>
                Cerrar
              </Button>
            </div>
          </ReactModal>
        );
      })()}

      <AnnulMonetizationDialog
        isOpen={annulDialogOpen}
        onClose={() => { setAnnulDialogOpen(false); setSelectedOngoingRow(null); }}
        onConfirm={handleOngoingAnnulConfirm}
        guarantee={selectedOngoingRow ? {
          id_global: selectedOngoingRow.id_global,
          monetization_amount: selectedOngoingRow.monetization_amount,
          application_product_line_id: selectedOngoingRow.application_product_line_id,
          product_label: selectedOngoingRow.product_line_label,
          currency_code: resolveCurrencyCode(selectedOngoingRow.currency_label),
        } : null}
        isLoading={annulling}
      />
    </Card>
    </>
  );
};

export default Monetization;
